import PropTypes from "prop-types";
import { Trans, useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import {
  Typography,
  Box,
  Link,
  GlobalStyles,
  useMediaQuery,
} from "@mui/material";
import { useScrollToTop } from "../../hooks/useScrollToTop";
import { replaceMainSiteLinkHostname } from "../../services/Utils";

import EnvelopeIcon from "../icons/EnvelopeIcon";
import OpenHandIcon from "../icons/OpenHandIcon";
import PencilIcon from "../icons/PencilIcon";
import ThankYouPageResourceCard from "../shared/ThankYouPageResourceCard";

const ThankYouPage = ({ filteredParams: _filteredParams }) => {
  const { t } = useTranslation("preSms");
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const VITE_SMS_NUMBER = import.meta.env.VITE_SMS_NUMBER || "988";

  useScrollToTop();

  const linkStyles = (theme) => ({
    color: theme.palette.primary.main,
    fontWeight: 700,
    fontSize: 16,
    fontFamily: "Public Sans, sans-serif",
    textDecoration: "underline",
    textUnderlineOffset: "4px",
    textDecorationThickness: "1px",
    outline: "1px dashed transparent",
    p: "1px",
    ":focus": {
      outline: `1px dashed ${theme.palette.secondary.main}`,
    },
    "&:hover": {
      color: theme.palette.tertiary.main,
      textDecorationColor: theme.palette.tertiary.main,
    },
  });

  const cards = [
    {
      IconComponent: OpenHandIcon,
      title: t("thankYouPage.visitSafeSpace"),
      description: t("thankYouPage.safeSpaceDescription"),
      linkHref: t("thankYouPage.safeSpaceUrl"),
      linkText: t("thankYouPage.safeSpaceLink"),
    },
    {
      IconComponent: EnvelopeIcon,
      title: t("thankYouPage.kindMindHeader"),
      description: t("thankYouPage.kindMindDescription"),
      linkHref: t("thankYouPage.kindMindLink"),
      linkText: t("thankYouPage.kindMindLinkText"),
    },
    {
      IconComponent: PencilIcon,
      title: t("thankYouPage.contactUsHeader"),
      description: t("thankYouPage.contactUsDescription"),
      linkHref: replaceMainSiteLinkHostname(
        t("thankYouPage.contactUsPageLink"),
      ),
      linkText: t("thankYouPage.contactUsPageLinkText"),
    },
  ];

  return (
    <>
      <GlobalStyles
        styles={{
          html: { backgroundColor: "#fff" },
          body: { backgroundColor: "#fff" },
        }}
      />

      <Box
        sx={{
          position: "relative",
          left: "50%",
          right: "50%",
          marginLeft: "-50vw",
          marginRight: "-50vw",
          width: "100vw",
          backgroundColor: "#fff",
        }}
      >
        <Box
          sx={{
            maxWidth: 1200,
            mx: "auto",
            px: { xs: 3, sm: 4 },
            pt: { xs: 6, md: 8 },
            pb: { xs: 10, md: 12 },
          }}
        >
          <Typography
            variant="h2"
            sx={{
              fontSize: isDesktop ? "31px" : "25px",
              textAlign: "center",
              mb: { xs: 2, md: 3 },
            }}
          >
            {t("thankYouPage.title")}
          </Typography>

          <Typography
            sx={{
              textAlign: "center",
              fontSize: 16,
              fontFamily: "Public Sans, sans-serif",
              lineHeight: "180%",
              mb: { xs: 2, md: 3 },
              maxWidth: 700,
              mx: "auto",
            }}
          >
            <Trans
              i18nKey="thankYouPage.subText"
              ns="preSms"
              components={{
                safeSpaceLink: (
                  <Link
                    href="https://safespace.vibrant.org/"
                    sx={{
                      ...linkStyles(theme),
                      color: "inherit",
                    }}
                  />
                ),
              }}
            />
          </Typography>

          <Typography
            sx={{
              textAlign: "center",
              fontSize: 16,
              fontFamily: "Public Sans, sans-serif",
              fontWeight: 700,
              mb: { xs: 4, md: 6 },
            }}
          >
            <Trans
              t={t}
              i18nKey="thankYouPage.returnToText"
              components={{
                returnLink: (
                  <Link
                    href={`sms:${VITE_SMS_NUMBER}`}
                    sx={linkStyles(theme)}
                  />
                ),
              }}
            />
          </Typography>

          <Box
            sx={{
              display: { xs: "block", md: "grid" },
              gridTemplateColumns: {
                md: "repeat(auto-fit, minmax(256px, 1fr))",
              },
              columnGap: { md: 4 },
              rowGap: { md: 4 },
              "& > * + *": { mt: { xs: 3, sm: 4, md: 0 } },
            }}
          >
            {cards.map((card) => (
              <ThankYouPageResourceCard key={card.title} {...card} />
            ))}
          </Box>
        </Box>
      </Box>
    </>
  );
};

ThankYouPage.propTypes = {
  filteredParams: PropTypes.object,
};

export default ThankYouPage;
